<!--
 * @Author: Tahiti
 * @Date: 2025-10-21 14:34:38
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-30 17:48:44
 * @Description: 
 * @FilePath: /weryai-web/components/Tool/Text-to-Video-Setting.vue
-->
<template>
    <div
        class="flex flex-col justify-between bg-setting-bg max-h-[inherit] w-full overflow-y-auto rounded-lg p-4 lg:p-6 lg:pb-0 lg:!w-[424px] lg:border-r lg:border-r-[#1D1D20]"
    >
        <div>
            <span
                class="hidden lg:block text-white cursor-default text-base font-semibold md:text-lg mb-[30px]"
            >
                {{ t('menus.textToVideo') }}
            </span>
            <tool-model-select
                v-model:model="edit_model"
                :model_list="modelList"
                :type="props.type"
            ></tool-model-select>
            <tool-model-version
                v-if="currentModelList?.length"
                v-model:model_version="model_version"
                :model_version_list="currentModelList"
            ></tool-model-version>
            <tool-prompt-textarea
                v-model:prompt="edit_prompt"
                :recommend_list="recommend_list"
                v-model:prompt_length_limit="prompt_length_limit"
            ></tool-prompt-textarea>
            <tool-prompt-textarea
                v-if="has_negative_prompt"
                v-model:prompt="negative_prompt"
                :title="t('tools.negativePrompt')"
                :placeholder="t('tools.describeNegativePrompt')"
                :is_show_recommend="false"
                v-model:prompt_length_limit="prompt_length_limit"
            ></tool-prompt-textarea>
            <!-- <tool-create-style v-model:style="edit_style"></tool-create-style> -->
            <tool-aspect-radio
                v-if="aspectRatioList?.length"
                v-model:aspect_ratio="edit_aspect_ratio"
                :aspect_ratio_list="aspectRatioList || []"
            ></tool-aspect-radio>
            <tool-duration-slider
                v-if="supportsDurationSlider && durationList?.length"
                v-model:duration="duration"
                :min="durationSliderMin"
                :max="durationSliderMax"
                :step="1"
                :default-value="durationSliderDefaultValue"
            ></tool-duration-slider>
            <tool-durations
                v-else-if="durationList?.length"
                v-model:duration="duration"
                :duration_list="durationList || []"
            ></tool-durations>
            <tool-video-size
                v-if="resolutionList?.length"
                v-model:video_size="video_size"
                :video_size_list="resolutionList || []"
            ></tool-video-size>
            <tool-image-number
                create_type="VIDEO"
                v-model:output_image_number="edit_output_image_number"
            ></tool-image-number>
            <tool-video-audio
                v-if="has_generate_audio"
                v-model:generate_audio="generate_audio"
            ></tool-video-audio>
            <tool-public-visibility
                v-model:public_visible="public_visible"
            ></tool-public-visibility>
            <tool-copyright-protection
                v-model:copyright_protection="copyright_protection"
            ></tool-copyright-protection>
        </div>

        <tool-credit-required
            class="lg:sticky lg:bottom-0 lg:py-4 lg:!mb-0 bg-setting-bg"
            :type="props.type"
            :create_btn_disable="props.create_btn_disable"
            workflowType="textToVideo"
            :m="model_version || edit_model"
            :p="edit_aspect_ratio"
            :t="duration"
            :n="edit_output_image_number"
            :ga="has_generate_audio ? generate_audio : false"
            :r="video_size"
            :loading="props.create_loading"
        ></tool-credit-required>
    </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from '~/composables/useI18n'
import type { SettingType } from '~/types'
import { ModelType } from '~/types'
import { useAppStore } from '~/stores/app'
import {
    aspectRatioEnums,
    videoSizeEnums,
    formatAndGroupData,
} from '~/utils/common'
import { emitSelectedModelData } from '~/utils/emitter'
import {
    isAlmightyReferenceSupported,
    isDance25Model,
} from '~/utils/videoModelCapabilities'
const appStore = useAppStore()
const modelData = computed(() => appStore.modelData)

const { t } = useI18n()
const props = withDefaults(
    defineProps<{
        type: any
        create_btn_disable: boolean
        create_loading: boolean
        recommend_list?: any[]
    }>(),
    {
        type: 'text_to_video',
        create_btn_disable: false,
        create_loading: false,
        recommend_list: () => [],
    }
)

const edit_model = defineModel('edit_model', {
    type: String,
    default: '',
})

const edit_prompt = defineModel('edit_prompt', {
    type: String,
    default: '',
})

const edit_style = defineModel('edit_style', {
    type: String,
    default: 'auto',
})

const edit_aspect_ratio = defineModel('edit_aspect_ratio', {
    type: String,
    default: '',
})

const edit_output_image_number = defineModel('edit_output_image_number', {
    type: Number,
    default: 1,
})

const video_size = defineModel('video_size')
const duration = defineModel<number | null>('duration', {
    default: null,
})
const negative_prompt = defineModel('negative_prompt', {
    type: String,
    default: '',
})
const generate_audio = defineModel('generate_audio', {
    type: Boolean,
    default: true,
})

const model_version = defineModel('model_version', {
    type: String,
    default: '',
})

const public_visible = defineModel('public_visible', {
    type: Boolean,
    default: true,
})
const copyright_protection = defineModel('copyright_protection', {
    type: Boolean,
    default: false,
})
const prompt_length_limit = ref<number>(5000)
const current_model_data = ref<any>(null)

const has_generate_audio = computed(() => {
    return current_model_data.value?.has_generate_audio
})

const isRunwayModel = computed(() => {
    return edit_model.value === 'RUNWAY' || edit_model.value === 'RUNWAY_4_5'
})

const effectiveModelKey = computed(() => {
    return model_version.value || edit_model.value
})

const supportsDurationSlider = computed(() => {
    return (
        isRunwayModel.value ||
        isAlmightyReferenceSupported(effectiveModelKey.value)
    )
})

const durationSliderOptions = computed(() => {
    if (!Array.isArray(durationList.value)) return []
    return durationList.value
        .map(item => Number(item))
        .filter(item => Number.isFinite(item))
})

const durationSliderMin = computed(() => {
    return durationSliderOptions.value[0] ?? 2
})

const durationSliderMax = computed(() => {
    return (
        durationSliderOptions.value[durationSliderOptions.value.length - 1] ??
        10
    )
})

const durationSliderDefaultValue = computed(() => {
    if (isRunwayModel.value) return 5
    return durationSliderOptions.value[0] ?? 5
})

const aspectRatioList = ref<Array<any> | null>(null)
const durationList = ref<Array<any> | null>(null)
const resolutionList = ref<Array<any> | null>(null)
const has_negative_prompt = ref(false)
const currentModelList = ref<any>(null)

const modelList = computed(() => {
    if (!modelData.value) return []

    const arr = modelData.value?.video_models[props.type] || {}
    const finalData = formatAndGroupData(arr)

    // 只有当 edit_model.value 完全为空且没有设置任何值时，才设置默认值
    // 如果已经通过路由参数或其他方式设置了值，就不再覆盖
    if (!edit_model.value && finalData.length > 0) {
        // 注释掉默认选择逻辑，让父组件控制默认值
        // edit_model.value = finalData[0]?.model_key || ''
    }

    return finalData
})

const getCurrentSettingData = (same_: any) => {
    emitSelectedModelData(same_)
    aspectRatioList.value = same_?.aspect_ratios
    edit_aspect_ratio.value = aspectRatioList.value?.[0] || ''

    const durations = same_?.durations || []
    durationList.value = isDance25Model(
        same_?.model_key || model_version.value || edit_model.value
    )
        ? durations.filter((item: unknown) => {
              const value = Number(item)
              return Number.isFinite(value) && value >= 4 && value <= 30
          })
        : durations
    duration.value = durationList.value?.[0]
    resolutionList.value = same_?.resolutions
    video_size.value = resolutionList.value?.[0] || ''
    has_negative_prompt.value = same_?.has_negative_prompt
    prompt_length_limit.value = same_?.prompt_length_limit || 5000
}

watch(
    [() => edit_model.value, () => modelData.value],
    ([newVal, newVal2]: [string, any]) => {
        if (newVal && newVal2) {
            let same_ = modelList.value?.find(
                (it: any) => it.model_key === newVal
            )

            current_model_data.value = same_
            currentModelList.value = same_?.model_version_list || []

            // 如果当前 model_version 已存在且在列表中，保持不变；否则设置默认值
            const currentVersion = model_version.value
            const isVersionInList = currentModelList.value?.find(
                (v: any) => v.model_key === currentVersion
            )

            if (!currentVersion || !isVersionInList) {
                model_version.value =
                    currentModelList.value?.[0]?.model_key || ''
            }

            same_ && getCurrentSettingData(same_)
        }
    },
    {
        immediate: true,
        flush: 'post',
    }
)

watch(
    [() => model_version.value, () => currentModelList.value],
    ([newVal, newVal2]: [string, any[]]) => {
        if (newVal && newVal2) {
            const same_ = currentModelList.value?.find(
                (it: any) => it.model_key === newVal
            )

            same_ && getCurrentSettingData(same_)
        }
    }
)
</script>
<style scoped lang="less">
.setting-side {
    width: 100%;
    height: 100%;
    background-color: #222222;
}
</style>
